import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import FilterMenu from "../../../components/FilterMenu";
import { ChartAreaLoader } from "../../../components/Loading";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import { SearchBox } from "../../../components/Search";
import { BrushStart } from "../../../models/chart_model";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "../../Rajasthan/Demand/SecondaryHeader";
import Tooltip from "../../../components/tooltip";
import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";
import { AddDatesWithFileName } from "../../../helpers/helper";
import { H_CHART_TYPES } from "../../Hppc/const";
import { formatDateForAPI } from "../const";
import { useLegendFilter } from "../../../hooks/useLegendFilter";
import { normalizeDisplayLabelWithoutUndefined } from "../../../utility/displayText";
import {
  getChhattisgarhRealTimeLoad,
  getChhattisgarhRealTimeLoadMetrics,
  ChhattisgarhRealtimeLoadItem,
} from "../../../Rest_api/RestApi";
import { getMonthWithYear } from "../Demand/Discom_Analysis";
import MetricChip from "../../../components/Metrics";
import { ChhattisgarhRealTimeLoadMetrics } from "../../../models/metrics";
import { ReactComponent as PeakIcon } from "../../../icons/peakicon.svg";
import { ReactComponent as AvgIcon } from "../../../icons/AvgIcon.svg";

const Intervals = [
  {
    label: "Time Slot",
    key: "slot",
    values: ["time_interval_number", "time_interval"],
    isTime: true,
  },
  { label: "Date", key: "date", values: ["date"], isTime: false },
  { label: "Month", key: "month", values: ["month"], isTime: false },
];

const UNITS = ["MW", "MWh", "LU"];
const TYPE_OPTIONS = ["Transformer", "Line"];
const FUNCTIONS = ["Comparative", "Cumulative"] as const;
type FunctionMode = (typeof FUNCTIONS)[number];
const POLL_INTERVAL_MS = 5 * 60 * 1000;

function extractVoltageLevel(name: string): string | null {
  const m = name.match(/\((\d+)\s*KV\)/i);
  return m ? `${m[1]} KV` : null;
}

function isToday(d: Date | string | undefined): boolean {
  if (!d) return false;
  const dt = typeof d === "string" ? new Date(d) : d;
  const today = new Date();
  return (
    dt.getFullYear() === today.getFullYear() &&
    dt.getMonth() === today.getMonth() &&
    dt.getDate() === today.getDate()
  );
}

const DemandRealtime = () => {
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );

  const [selectedUnit, setSelectedUnit] = useState(UNITS[0]);
  const [selectedInterval, setSelectedInterval] = useState(Intervals[0]);
  const [selectedChartType, setSelectedChartType] = useState(H_CHART_TYPES[1]);
  const [selectedFunction, setSelectedFunction] =
    useState<FunctionMode>("Comparative");
  const [selectedTypes, setSelectedTypes] = useState<string[]>([
    ...TYPE_OPTIONS,
  ]);
  const [selectedVoltages, setSelectedVoltages] = useState<string[]>([]);

  const [data, setData] = useState<ChhattisgarhRealtimeLoadItem[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [TableData, setTableData] = useState<any[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [selectedNames, setSelectedNames] = useState<
    { label: string; value: string }[]
  >([]);
  const [loading, setLoading] = useState(true);

  const [metrics, setMetrics] = useState<ChhattisgarhRealTimeLoadMetrics | null>(
    null,
  );
  const [metricsLoading, setMetricsLoading] = useState(false);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const _handleFetchData = useCallback(
    async (silent = false) => {
      if (!DefaultDates?.[activeTab]?.defaultRangeDate) return;
      const dates = DefaultDates[activeTab].defaultRangeDate;
      if (!silent) setLoading(true);
      const startDateStr = formatDateForAPI(dates.startDate);
      const endDateStr = formatDateForAPI(dates.endDate);
      try {
        const res = await getChhattisgarhRealTimeLoad({
          startDate: startDateStr,
          endDate: endDateStr,
        });

        const dedup = new Map<string, ChhattisgarhRealtimeLoadItem>();
        res.forEach((item) => {
          const key = `${item.date}_${item.time_interval_number}_${item.transformer_or_line}_${item.category}`;
          if (!dedup.has(key)) {
            const mw = Number(item.value) || 0;
            dedup.set(key, {
              ...item,
              value_mw: mw,
              value_mwh: mw / 12,
              value_lu: mw / 1200,
            });
          }
        });
        setData(Array.from(dedup.values()));
      } catch (error) {
        console.error("Error fetching realtime load:", error);
        setData([]);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [DefaultDates, activeTab],
  );

  const fetchMetrics = useCallback(async () => {
    if (!DefaultDates?.[activeTab]?.defaultRangeDate) return;
    const dates = DefaultDates[activeTab].defaultRangeDate;
    if (!dates?.startDate) return;

    setMetricsLoading(true);
    try {
      const res = await getChhattisgarhRealTimeLoadMetrics({
        date: formatDateForAPI(dates.startDate),
        entities:
          selectedNames.length > 0
            ? selectedNames.map((n) => n.value)
            : undefined,
        blocks:
          selectedBlock.length > 0
            ? selectedBlock.map((b) => b.value)
            : undefined,
      });
      setMetrics(res);
    } catch (e) {
      console.error("Error fetching metrics:", e);
    } finally {
      setMetricsLoading(false);
    }
  }, [DefaultDates, activeTab, selectedNames, selectedBlock]);

  useEffect(() => {
    fetchMetrics();
    // Refresh metrics every 5 mins if live
    const interval = setInterval(() => {
      const dates = DefaultDates[activeTab]?.defaultRangeDate;
      if (isToday(dates?.startDate) && isToday(dates?.endDate)) {
        fetchMetrics();
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchMetrics, DefaultDates, activeTab]);

  useEffect(() => {
    _handleFetchData();
  }, [_handleFetchData]);

  // Polling: only when both start/end are today
  useEffect(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    const dates = DefaultDates?.[activeTab]?.defaultRangeDate;
    if (!dates) return;
    if (isToday(dates.startDate) && isToday(dates.endDate)) {
      pollRef.current = setInterval(() => {
        _handleFetchData(true);
      }, POLL_INTERVAL_MS);
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [DefaultDates, activeTab, _handleFetchData]);

  // Unique names per category from raw data
  const namesByCategory = useMemo(() => {
    const map: Record<string, Set<string>> = {
      Transformer: new Set(),
      Line: new Set(),
    };
    data.forEach((item) => {
      if (!item.transformer_or_line) return;
      const cat = item.category === "Line" ? "Line" : "Transformer";
      map[cat].add(item.transformer_or_line);
    });
    return {
      Transformer: Array.from(map.Transformer),
      Line: Array.from(map.Line),
    };
  }, [data]);

  const allNameOptions = useMemo(() => {
    const opts: { label: string; value: string; category: string }[] = [];
    namesByCategory.Transformer.forEach((n) =>
      opts.push({ label: n, value: n, category: "Transformer" }),
    );
    namesByCategory.Line.forEach((n) =>
      opts.push({ label: n, value: n, category: "Line" }),
    );
    return opts;
  }, [namesByCategory]);

  // Voltage levels derived from all names
  const voltageOptions = useMemo(() => {
    const seen = new Set<string>();
    allNameOptions.forEach((o) => {
      const v = extractVoltageLevel(o.value);
      if (v) seen.add(v);
    });
    return Array.from(seen).sort((a, b) => parseInt(b) - parseInt(a));
  }, [allNameOptions]);

  const [namesInitialized, setNamesInitialized] = useState(false);
  useEffect(() => {
    if (namesInitialized || allNameOptions.length === 0) return;
    const first2 = allNameOptions.slice(0, 2).map((o) => ({
      label: o.label,
      value: o.value,
    }));
    setSelectedNames(first2);
    setNamesInitialized(true);
  }, [allNameOptions, namesInitialized]);

  const prevVoltages = useRef(selectedVoltages);
  useEffect(() => {
    if (prevVoltages.current !== selectedVoltages) {
      prevVoltages.current = selectedVoltages;
      if (selectedVoltages.length > 0) {
        const matchingNames = allNameOptions
          .filter((o) => {
            const v = extractVoltageLevel(o.value);
            return v !== null && selectedVoltages.includes(v);
          })
          .map((o) => ({ label: o.label, value: o.value }));
        setSelectedNames(matchingNames);
      }
    }
  }, [selectedVoltages, allNameOptions]);

  function handleTypeChange(items: string[]) {
    setSelectedTypes(items);
    const next = allNameOptions
      .filter((o) => items.includes(o.category))
      .map((o) => ({ label: o.label, value: o.value }));
    setSelectedNames(next);
  }

  function handleNameChange(opts: any[]) {
    setSelectedNames(opts ?? []);
  }

  // Build chart data
  useEffect(() => {
    let filtered = data
      .filter((item) => selectedTypes.includes(item.category))
      .map((item) => ({
        ...item,
        month: item.date ? getMonthWithYear(item.date) : item.month,
      }));

    // Voltage level filter
    if (selectedVoltages.length > 0) {
      filtered = filtered.filter((item) => {
        const v = extractVoltageLevel(item.transformer_or_line ?? "");
        return v !== null && selectedVoltages.includes(v);
      });
    }

    if (selectedNames.length > 0) {
      const selSet = new Set(selectedNames.map((s) => s.value));
      filtered = filtered.filter((item) =>
        selSet.has(item.transformer_or_line),
      );
    }

    if (selectedBlock.length > 0) {
      const blkSet = new Set(selectedBlock.map((b) => b.value));
      filtered = filtered.filter((item) =>
        blkSet.has(item.time_interval_number),
      );
    }

    filtered.sort((a, b) => {
      const dc = new Date(a.date).getTime() - new Date(b.date).getTime();
      if (dc !== 0) return dc;
      return (a.time_interval_number || 0) - (b.time_interval_number || 0);
    });

    const processedRows = filtered.map((item) => ({
      ...item,
      series_group:
        selectedFunction === "Cumulative"
          ? "Total"
          : item.transformer_or_line,
    }));

    function getKeys() {
      if (selectedInterval.key === "slot")
        return ["date", "time_interval_number", "time_interval"];
      if (selectedInterval.key === "date") return ["date"];
      return [selectedInterval.values[0]];
    }

    const par: ChartDataParams = {
      keys: getKeys(),
      groupBy: ["series_group"],
      tableKeys: ["date", "time_interval_number", "time_interval", "month"],
      volumeKey: "",
      aggregationKeys: [
        "value_mw",
        "value_mwh",
        "value_lu",
        "max",
        "min",
        "avg",
      ],
      priceKey: "",
      averageKeys: ["value_mw", "value_mwh", "value_lu", "max", "min", "avg"],
      averageDuplicationKeys: ["date", "time_interval_number", "month"],
      data: processedRows,
    };
    const cData = FormatChartData(par);

    setChartData(cData.data);
    setChartKeys(cData.keys as string[]);
    setTableData(cData.tableData || []);
  }, [
    data,
    selectedInterval,
    selectedBlock,
    selectedNames,
    selectedTypes,
    selectedVoltages,
    selectedUnit,
    selectedFunction,
  ]);

  const xDataKey = useMemo(() => {
    if (selectedInterval.isTime) return "time_interval_number";
    return selectedInterval.key;
  }, [selectedInterval]);

  function secondDataKey() {
    if (selectedInterval.isTime) return "time_interval";
    return undefined;
  }

  const summaryExtraKeys = selectedInterval.isTime
    ? ["time_interval_number", "time_interval"]
    : [selectedInterval.values[0]];

  const summaryExtraLabels = selectedInterval.isTime
    ? ["Slot", "Time"]
    : selectedInterval.values.map((v) => (v === "date" ? "Date" : v));

  function buildLegends() {
    return chartKeys.map((key) => ({
      dataKey: `${key}_value_${selectedUnit.toLowerCase()}`,
      name: normalizeDisplayLabelWithoutUndefined(key),
      legendColor: undefined,
      type: selectedChartType.value,
      stackId:
        (selectedChartType as any).stackId !== undefined ? "load" : undefined,
    }));
  }

  const { visibleLegends, handleLegendSelect, selectedLegends } =
    useLegendFilter(buildLegends());

  const tablePar = {
    legends: visibleLegends,
    data: chartData,
    tableData: TableData,
    itemsPerPage: 288,
    block_key: "time_interval_number",
    unit: selectedUnit,
    extraKeys: summaryExtraKeys,
    extraLabels: summaryExtraLabels,
    filename: AddDatesWithFileName({
      fileName: "CSPDCL Realtime Load",
      dates: DefaultDates[activeTab]?.defaultRangeDate || [],
    }),
  };

  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="Intervals"
            items={Intervals.map((i) => i.label)}
            selectedItems={[selectedInterval.label]}
            onSelect={(_item, index) => setSelectedInterval(Intervals[index])}
          />
          <FilterMenu
            title="Unit"
            items={UNITS}
            selectedItems={[selectedUnit]}
            onSelect={(item) => setSelectedUnit(item)}
          />
          <FilterMenu
            title="Function"
            items={[...FUNCTIONS]}
            selectedItems={[selectedFunction]}
            onSelect={(_item, index) => setSelectedFunction(FUNCTIONS[index])}
          />
          <FilterMenu
            title="Type"
            items={TYPE_OPTIONS}
            selectedItems={selectedTypes}
            setSelectedItems={handleTypeChange}
          />

          {/* {voltageOptions.length > 0 && (
            <FilterMenu
              title="Voltage"
              items={voltageOptions}
              selectedItems={selectedVoltages}
              setSelectedItems={setSelectedVoltages}
            />
          )} */}
          <FilterMenu
            title="Chart Type"
            items={H_CHART_TYPES.map((i) => i.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(_item, index) =>
              setSelectedChartType(H_CHART_TYPES[index])
            }
          />
        </div>
      </NewFilterContainer>
      <MainPageContainer>
        <SecondaryHeader />
        {/* {metrics && (
          <div className="flex flex-wrap gap-4 my-0 justify-center">
            <MetricChip
              icon={<AvgIcon width={25} />}
              items={[
                {
                  label: "Current Demand",
                  value: metrics?.["Current Demand (MW)"]?.value,
                  unit: metrics?.["Current Demand (MW)"]?.unit || "MW",
                },
              ]}
            />

            <MetricChip
              icon={<PeakIcon width={25} />}
              items={[
                {
                  label: `Peak Today (${metrics?.["Peak Demand Today (MW)"]?.peak_time || "--"})`,
                  value: metrics?.["Peak Demand Today (MW)"]?.value,
                  unit: metrics?.["Peak Demand Today (MW)"]?.unit || "MW",
                },
              ]}
            />
            <MetricChip
              icon={<PeakIcon width={25} />}
              items={[
                {
                  label: `Ramp Rate (${metrics?.["Ramp Rate (MW/5-min)"]?.label || "--"})`,
                  value: metrics?.["Ramp Rate (MW/5-min)"]?.value,
                  unit: metrics?.["Ramp Rate (MW/5-min)"]?.unit || "MW/5-min",
                },
              ]}
            />
          </div>
        )} */}
        <div className="gas-chart-container">
          {loading ? (
            <ChartAreaLoader
              title="Loading Realtime Load"
              subtitle="Fetching Chhattisgarh real-time transformer/line load…"
            />
          ) : (
            <>
              <div className="gas-heading gap-x-10">
                <h2 className="chartHeading flex items-center gap-x-4">
                  Demand ({selectedUnit})
                  {isToday(
                    DefaultDates[activeTab]?.defaultRangeDate?.startDate,
                  ) &&
                    isToday(
                      DefaultDates[activeTab]?.defaultRangeDate?.endDate,
                    ) && (
                      <span
                        title="Live — auto-refreshes every 5 minutes"
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        <span className="gna-live-sonar">
                          <span className="gna-live-sonar__dot" />
                          <span className="gna-live-sonar__ring" />
                        </span>
                        <span
                          style={{
                            fontSize: 11,
                            color: "#22c55e",
                            fontWeight: 600,
                            letterSpacing: 0.5,
                          }}
                        >
                          LIVE
                        </span>
                      </span>
                    )}
                </h2>
                <div className="flex gap-x-6 justify-end items-center">
                  <div style={{ minWidth: 220 }}>
                    <SearchBox
                      isMany
                      options={allNameOptions.map((o) => ({
                        label: o.label,
                        value: o.value,
                      }))}
                      selectedOptions={selectedNames}
                      onChange={handleNameChange}
                      placeholder="Search transformer / line"
                    />
                  </div>
                  <BlockRangeSelector
                    blocks={Array.from({ length: 288 }, (_, i) => i + 1)}
                    placeholder="Search Block"
                    selectedOptions={selectedBlock.map((item) => item.value)}
                    setSelectedOptions={setSelectedBlock}
                    blockMinutes={5}
                  />
                  <button
                    onClick={() => {
                      dataTab.label === "Chart" &&
                        downloadChartTableCSV(tablePar);
                      dataTab.label === "Summary" &&
                        downloadSummaryCSV(tablePar);
                      dataTab.label === "Table" &&
                        downloadChartTableCSV(tablePar);
                    }}
                    className="downloadButton"
                  >
                    Download Data
                  </button>
                </div>
              </div>

              {dataTab.label === "Chart" && (
                <ReMixChart
                  data={chartData}
                  showBrush={chartData.length > 30 ? true : undefined}
                  brushStart={BrushStart.Start}
                  isTimeSlot={false}
                  onlyTitle={true}
                  barCategoryGap={10}
                  unit={selectedUnit}
                  yAxisLabel={selectedUnit}
                  legends={buildLegends()}
                  onLegendSelect={handleLegendSelect}
                  defaultSelectedLegends={selectedLegends}
                  xDataKey={xDataKey}
                  secondXDataKey={secondDataKey()}
                />
              )}
              {dataTab.label === "Summary" && (
                <LegendSummaryTable {...tablePar} />
              )}
              {dataTab.label === "Table" && <ChartTable {...tablePar} />}
            </>
          )}
        </div>
        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5">
            <Tooltip
              note={<p>CSPDCL</p>}
              text="
            1. Realtime load data sourced from CSPDCL feeds."
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
};

export default DemandRealtime;
